import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import styles from '../styles/TermsOfService.module.css'
import FooterSection from './FooterSection'
import ScrollToTop from '../../../components/ScrollToTop/ScrollToTop'
import footerLogo from '../../../assets/image/logo/thickWalletOnly.png'

const PrivacyPolicy = () => {
    const history = useHistory();
    const lastUpdated = "December 22, 2025";

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleBackClick = () => {
        history.push('/');
    };

    const handleBackKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleBackClick();
        }
    };

    const handleTermsClick = () => {
        history.push('/terms');
    };

    const handleTermsKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTermsClick();
        }
    };

    return (
        <div className={styles.terms_page}>
            <main className={styles.terms_main}>
                <div className="container">
                    <button 
                        className={styles.back_button}
                        onClick={handleBackClick}
                        onKeyDown={handleBackKeyDown}
                        tabIndex={0}
                        aria-label="Back to Home"
                    >
                        <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <path 
                                d="M19 12H5M5 12L12 19M5 12L12 5" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            />
                        </svg>
                        <span>Back to Home</span>
                    </button>
                    <header className={styles.terms_header}>
                        <img 
                            src={footerLogo} 
                            alt="Thick Wallet Logo" 
                            className={styles.header_logo}
                        />
                        <h1 className={styles.terms_title}>Privacy Policy</h1>
                        <p className={styles.terms_subtitle}>
                            Last Updated: {lastUpdated}
                        </p>
                    </header>

                    <section className={styles.terms_content}>
                        <div className={styles.terms_section}>
                            <h2>1. Introduction</h2>
                            <p>
                                Welcome to <strong>Thick Wallet</strong>. We are committed to protecting your personal 
                                data and your privacy. This Privacy Policy explains how we collect, use, and share 
                                information when you use our web application to gain insights into the Indian stock 
                                market (NSE/BSE).
                            </p>
                            <p>
                                This policy is designed to comply with the <strong>Information Technology Act, 2000</strong>, 
                                the <strong>Digital Personal Data Protection (DPDP) Act, 2023</strong>, and applicable 
                                SEBI guidelines regarding financial data.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>2. Information We Collect</h2>
                            <p>
                                We collect information to provide a personalized experience and better stock insights:
                            </p>
                            <ul>
                                <li>
                                    <strong>Identity Data:</strong> Name, age, and gender (collected via Google/Facebook SSO)
                                </li>
                                <li>
                                    <strong>Contact Data:</strong> Email address and phone number
                                </li>
                                <li>
                                    <strong>Financial Interests:</strong> Stock watchlists, sectors you follow, and your 
                                    preference for short-term vs. long-term investments
                                </li>
                                <li>
                                    <strong>Technical Data:</strong> IP address, browser type, device information, and 
                                    usage patterns
                                </li>
                            </ul>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>3. How We Use Your Information</h2>
                            <p>
                                We use your data in the following ways:
                            </p>
                            <ul>
                                <li>To authenticate your login via SSO (Google/Facebook)</li>
                                <li>To provide customized stock insights and market alerts based on your interests</li>
                                <li>To maintain and improve your watchlists and preferences</li>
                                <li>To improve our algorithms for Indian market analysis</li>
                                <li>To communicate important updates about the Service</li>
                                <li>To comply with SEBI regulations and Indian legal requirements</li>
                            </ul>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>4. SEBI Disclaimer & Financial Data</h2>
                            <div className={styles.regulatory_notice}>
                                <p>
                                    <strong>Regulatory Notice:</strong> Thick Wallet provides stock market insights and 
                                    data analysis for <strong>educational and informational purposes only</strong>. We are 
                                    not a SEBI-registered Investment Advisor (RIA) or Research Analyst (RA).
                                </p>
                                <p>
                                    Users are advised to consult with a certified financial advisor before making any 
                                    actual investment decisions in the Indian markets.
                                </p>
                            </div>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>5. Data Sharing and Disclosure</h2>
                            <p>
                                <strong>We do not sell your personal data.</strong> We may share information only with:
                            </p>
                            <ul>
                                <li>
                                    <strong>Cloud Service Providers:</strong> To host the application securely
                                </li>
                                <li>
                                    <strong>Analytics Partners:</strong> To understand how users interact with our 
                                    stock charts and features (anonymized data only)
                                </li>
                                <li>
                                    <strong>Legal Authorities:</strong> If required by SEBI, Indian law enforcement, 
                                    or court order
                                </li>
                            </ul>
                            <p>
                                Any third-party service providers are bound by confidentiality agreements and are 
                                only permitted to use your data as necessary to provide services to us.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>6. Data Security</h2>
                            <p>
                                We implement industry-standard security measures to protect your data:
                            </p>
                            <ul>
                                <li>SSL/TLS encryption for all data in transit</li>
                                <li>Encrypted storage for sensitive information</li>
                                <li>Regular security audits and vulnerability assessments</li>
                                <li>Access controls limiting employee access to personal data</li>
                            </ul>
                            <p>
                                Since we use Google and Facebook for SSO, <strong>your passwords are never stored 
                                on our servers</strong>, significantly reducing security risks.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>7. User Rights Under DPDP Act 2023</h2>
                            <p>
                                Under the Digital Personal Data Protection Act, 2023, you have the following rights:
                            </p>
                            <ul>
                                <li>
                                    <strong>Right to Access:</strong> Request access to the personal data we hold about you
                                </li>
                                <li>
                                    <strong>Right to Correction:</strong> Request correction of any inaccuracies in your data
                                </li>
                                <li>
                                    <strong>Right to Erasure:</strong> Request deletion of your personal data (subject to 
                                    legal retention requirements)
                                </li>
                                <li>
                                    <strong>Right to Grievance Redressal:</strong> Lodge complaints regarding data processing
                                </li>
                                <li>
                                    <strong>Right to Nominate:</strong> Nominate another individual to exercise your rights 
                                    in case of death or incapacity
                                </li>
                            </ul>
                            <div className={styles.contact_info}>
                                <p>
                                    <strong>To exercise these rights:</strong> You can delete your account via the settings 
                                    page, or email us at <strong>info@thick-wallet.com</strong> to request data access, 
                                    correction, or complete data erasure.
                                </p>
                            </div>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>8. Data Retention</h2>
                            <p>
                                We retain your personal data only for as long as necessary to:
                            </p>
                            <ul>
                                <li>Provide the services you have requested</li>
                                <li>Comply with legal obligations under Indian law</li>
                                <li>Resolve disputes and enforce our agreements</li>
                            </ul>
                            <p>
                                Upon account deletion, your personal data will be removed from our active systems 
                                within 30 days, except where retention is required by law.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>9. Cookies & Tracking Technologies</h2>
                            <p>
                                We use cookies and similar technologies to:
                            </p>
                            <ul>
                                <li>Remember your stock watchlists and session preferences</li>
                                <li>Analyze site traffic and user behavior (using anonymized data)</li>
                                <li>Improve user experience and personalization</li>
                            </ul>
                            <p>
                                You can disable cookies in your browser settings, but some features of the app 
                                may stop working properly.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>10. Third-Party Authentication</h2>
                            <p>
                                When you log in using Google or Facebook SSO, we receive limited information from 
                                these services based on your privacy settings:
                            </p>
                            <ul>
                                <li>Your name and email address</li>
                                <li>Profile picture (if made available)</li>
                                <li>Account verification status</li>
                            </ul>
                            <p>
                                We do not have access to your social media passwords or post content. Please review 
                                the privacy policies of{' '}
                                <a 
                                    href="https://policies.google.com/privacy" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    aria-label="Google Privacy Policy (opens in new tab)"
                                >
                                    Google
                                </a>
                                {' '}and{' '}
                                <a 
                                    href="https://www.facebook.com/policy.php" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    aria-label="Facebook Privacy Policy (opens in new tab)"
                                >
                                    Facebook
                                </a>
                                {' '}for more information about how they handle your data.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>11. Children's Privacy</h2>
                            <p>
                                Thick Wallet is not intended for use by individuals under the age of 18. We do not 
                                knowingly collect personal data from children. If you believe we have inadvertently 
                                collected data from a minor, please contact us immediately.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>12. Changes to This Privacy Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time. If we make material changes, 
                                we will notify you by email or by posting a notice on our Service. Your continued 
                                use of the Service after the changes take effect constitutes your acceptance of 
                                the updated policy.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>13. Grievance Officer</h2>
                            <p>
                                In accordance with the Information Technology Act and DPDP Act, if you have any 
                                questions, concerns, or complaints about our data practices, please contact our 
                                Grievance Officer:
                            </p>
                            <div className={styles.contact_info}>
                                <p><strong>Email:</strong> info@thick-wallet.com</p>
                                <p><strong>Website:</strong> thick-wallet.com</p>
                                <p><strong>Response Time:</strong> We will respond to grievances within 30 days</p>
                            </div>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>14. Data Deletion Request (For Facebook Users)</h2>
                            <p>
                                If you have logged in using Facebook and wish to delete your data, you can:
                            </p>
                            <ul>
                                <li>Delete your account through our app settings</li>
                                <li>Email us at <strong>info@thick-wallet.com</strong> with subject "Data Deletion Request"</li>
                                <li>Remove Thick Wallet access from your Facebook settings</li>
                            </ul>
                            <p>
                                Upon receiving your request, we will delete all your personal data within 30 days 
                                and send you a confirmation email.
                            </p>
                        </div>

                        <div className={styles.terms_footer}>
                            <p>
                                By using Thick Wallet, you acknowledge that you have read and understood this 
                                Privacy Policy. For our complete terms of use, please read our{' '}
                                <span 
                                    className={styles.link_text}
                                    onClick={handleTermsClick}
                                    onKeyDown={handleTermsKeyDown}
                                    tabIndex={0}
                                    role="link"
                                    aria-label="View Terms of Service"
                                >
                                    Terms of Service
                                </span>.
                            </p>
                        </div>
                    </section>
                </div>
            </main>
            <ScrollToTop />
            <FooterSection />
        </div>
    )
}

export default PrivacyPolicy

